/**
 * Client notes (Mon Ami's Note: CaseNote and BasicNote).
 *
 * A note written in GO lives under its offline_uuid until it uploads, then
 * under the server id. Uploaded notes are read-only in GO.
 */

export type CaseNoteType = 'CaseNote' | 'BasicNote';

export interface NamedRecord {
  id: number;
  name: string;
}

export interface NoteServiceUnit {
  service_definition_id: number;
  name?: string;
  /** Decimal string, as Mon Ami sends it ("1.5"). */
  unit_count: string;
  unit_name?: string;
  funding_source: NamedRecord | null;
}

export interface CaseNote {
  /** offline_uuid until the note uploads, then the server id. */
  id: number | string;
  /** Set for notes written in GO; null for notes written in Mon Ami. */
  offline_uuid: string | null;
  type: CaseNoteType;
  client_id: number;
  organization_id: number;
  /** YYYY-MM-DD */
  interaction_date: string;
  /** "Coding" for a case note, "Source" for a basic note. */
  interaction_type: string | null;
  detail: string;
  subject: string | null;
  note_subject: NamedRecord | null;
  check_in: string | null;
  pinned: boolean;
  program: NamedRecord | null;
  site: NamedRecord | null;
  author: { id: number; name: string; email?: string | null } | null;
  service_units: NoteServiceUnit[];
  created_at: string;
  updated_at: string;
  /** False while the note exists only on this device. */
  synced: boolean;
  /** When this device uploaded it (device clock); guards the refresh prune. */
  uploaded_at?: string | null;
  /** Why Mon Ami refused the note. The sweep holds refused notes until they are edited or retried. */
  sync_error_message?: string | null;
  sync_error_at?: string | null;
}

/** A note as the offline API returns it (index, show, create). */
export type ServerCaseNote = Omit<CaseNote, 'client_id' | 'organization_id' | 'synced' | 'uploaded_at' | 'sync_error_message' | 'sync_error_at'> & {
  id: number;
};

export interface NoteOption {
  value: string;
  label: string;
}

export type NoteSubjectMode = 'none' | 'preset' | 'free_text' | 'preset_or_free_text';

export interface NoteServiceDefinitionOption {
  id: number;
  name: string;
  unit_name: string;
  accepts_partial_units: boolean;
  funding_sources: NamedRecord[];
  default_funding_source_id: number | null;
}

export interface NoteProgramOption {
  id: number;
  name: string;
  uses_coding: boolean;
  uses_check_in: boolean;
  subject: { mode: NoteSubjectMode; options: NamedRecord[] };
  service_definitions: NoteServiceDefinitionOption[];
}

/** What the note form offers this person, from GET /api/offline/note_options. No PHI. */
export interface NoteOptions {
  organization_id: number;
  /** Types this person can create. */
  note_types: CaseNoteType[];
  requires_site: boolean;
  default_site_id: number | null;
  sites: NamedRecord[];
  coding_options: NoteOption[];
  source_options: NoteOption[];
  check_in_options: NoteOption[];
  programs: NoteProgramOption[];
}

/** Pinned first, then by date of interaction, newest first — the order Mon Ami uses. */
export function sortCaseNotes(notes: CaseNote[]): CaseNote[] {
  return [...notes].sort((a, b) =>
    Number(b.pinned) - Number(a.pinned) ||
    b.interaction_date.localeCompare(a.interaction_date) ||
    b.created_at.localeCompare(a.created_at)
  );
}

export function isPendingCaseNote(note: Pick<CaseNote, 'synced'>): boolean {
  return !note.synced;
}

/** The label Mon Ami shows for a note's interaction type, falling back to a humanized value. */
export function interactionTypeLabel(note: Pick<CaseNote, 'type' | 'interaction_type'>, options?: NoteOptions | null): string | null {
  if (!note.interaction_type) return null;
  const list = note.type === 'BasicNote' ? options?.source_options : options?.coding_options;
  const match = list?.find(option => option.value === note.interaction_type);
  if (match) return match.label;
  return note.interaction_type.replace(/_/g, ' ').replace(/^\w/, c => c.toUpperCase());
}

/** "1.5 hours" */
export function formatUnitCount(unit: Pick<NoteServiceUnit, 'unit_count' | 'unit_name'>): string {
  const count = Number(unit.unit_count);
  return unit.unit_name ? `${count} ${unit.unit_name}${count === 1 ? '' : 's'}` : String(count);
}

/** "1.5 hours Case Management" for each unit with a count. */
export function describeServiceUnits(units: NoteServiceUnit[]): string[] {
  return units
    .filter(unit => Number(unit.unit_count) > 0)
    .map(unit => `${formatUnitCount(unit)} ${unit.name ?? ''}`.trim());
}
